import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ContractComment } from '@core/models/contract-comment/contract-comment.model';
import { Contract } from '@core/models/contract.model';
import { TvConfirmationData } from '@core/models/tv-confirmation-data.model';
import { AuthService } from '@core/services/auth.service';
import { ContractCommentService } from '@core/services/contract-comment/contract-comment.service';
import { ContractFilter, ContractService } from '@core/services/contract/contract.service';
import { CommentAddDialogComponent } from '@shared/dialog/comment-add-dialog/comment-add-dialog.component';
import { CommentEditDialogComponent } from '@shared/dialog/comment-edit-dialog/comment-edit-dialog.component';
import { TvConfirmPopupComponent } from '@shared/dialog/tv-confirm-popup/tv-confirm-popup.component';
import { Subject, merge } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
@Component({
  standalone: false,
  selector: 'app-contract-comment',
  templateUrl: './contract-comment.component.html',
  styleUrls: ['./contract-comment.component.scss'],
})
export class ContractCommentComponent implements OnInit, OnDestroy {
  private readonly yearUrlIndex = 3;
  private readonly contractIdUrlIndex = 4;

  public currentContract: Contract;
  public contractFilter: ContractFilter;

  public searchFormControl = new FormControl();
  public screenFilterForm: any;

  public comments: ContractComment[] = [];
  public filteredComments: ContractComment[] = [];

  public panelOpenState: boolean;
  public commentIsEmpty: boolean;
  public currentUserName: string;

  private contractId: number;

  private readonly destroy$ = new Subject<void>();

  public readonly commentTypeLabel: Record<number, string> = {
    1: 'Classique',
    2: 'Digital',
    3: 'Parrainage',
  };

  constructor(
    private contractCommentService: ContractCommentService,
    private userDataService: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    private contractService: ContractService,
    private fb: FormBuilder
  ) {
    this.screenFilterForm = this.fb.group({
      startDate: [null],
      endDate: [null],
    });
  }

  ngOnInit(): void {
    this.handleUserDataSubscription();
    this.getContractFilterParams();
    this.handleContractSubscription(this.contractFilter);

    merge(
      this.searchFormControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()),
      this.screenFilterForm.get('startDate').valueChanges,
      this.screenFilterForm.get('endDate').valueChanges
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.filterComments(this.searchFormControl.value));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getContractFilterParams(): void {
    this.contractFilter = {
      year: this.router.url.split('/')[this.yearUrlIndex],
      searchElementById: this.router.url.split('/')[this.contractIdUrlIndex] || '',
    };
  }

  private handleUserDataSubscription(): void {
    this.userDataService
      .getUserInfo()
      .pipe(takeUntil(this.destroy$))
      .subscribe(userData => {
        this.currentUserName = `${userData.name}`;
      });
  }

  public handleContractSubscription(filter: ContractFilter): void {
    this.contractService
      .getContractList(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        contract => {
          if (contract[0]) {
            this.currentContract = contract[0];
            this.contractId = Number(this.currentContract.advertiserId);
            this.handleContractCommentSubscription();
          }
        },
        () => {
          this.snackBar.open('Erreur lors de la récupération des contrats', null, {
            duration: 4000,
            verticalPosition: 'top',
            panelClass: ['tv-chip-error'],
          });
        }
      );
  }

  public handleContractCommentSubscription(): void {
    if (!this.contractId) {
      this.comments = [];
      this.filteredComments = [];
      this.commentIsEmpty = true;
      return;
    }

    this.contractCommentService
      .getCommentsFor1Contract(this.contractId.toString())
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        data => {
          this.comments = data ?? [];
          this.filterCommentsByDate();
        },
        () => {
          this.snackBar.open('Erreur lors de la récupération des commentaires du contrat: ' + this.contractId, null, {
            duration: 4000,
            verticalPosition: 'top',
            panelClass: ['tv-chip-error'],
          });
        }
      );
  }

  openAddDialog(): void {
    const emptyComment: ContractComment = {
      id: null,
      author: this.currentUserName,
      body: '',
      commentDate: new Date(),
      contractId: Number(this.currentContract.advertiserId),
      bactif: 1,
      type: 1,
      editedBy: null,
      editedAt: null,
    };

    const dialogRef = this.dialog.open(CommentAddDialogComponent, {
      data: { comment: emptyComment },
      width: '1000px',
      panelClass: 'tv-dialog-container',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (!result) return;

        this.contractCommentService
          .addComment(result)
          .pipe(
            takeUntil(this.destroy$),
            catchError(error => {
              console.error("Erreur lors de l'ajout du commentaire", error);
              this.snackBar.open("Erreur lors de l'ajout du commentaire", null, {
                duration: 4000,
                verticalPosition: 'top',
                panelClass: ['tv-chip-error'],
              });
              throw error;
            })
          )
          .subscribe({
            next: () => {
              this.snackBar.open('Commentaire ajouté avec succès', null, {
                duration: 4000,
                verticalPosition: 'top',
                panelClass: ['tv-chip-success'],
              });
              this.handleContractCommentSubscription();
            },
          });
      });
  }

  openEditDialog(comment: ContractComment): void {
    const dialogRef = this.dialog.open(CommentEditDialogComponent, {
      data: { comment },
      panelClass: 'tv-dialog-container',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.updateComment(result);
        }
      });
  }

  updateComment(comment: ContractComment): void {
    comment.editedBy = this.currentUserName;
    comment.editedAt = new Date();

    this.contractCommentService
      .updateComment(comment)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.snackBar.open('Commentaire mis à jour avec succès', null, {
          duration: 4000,
          verticalPosition: 'top',
          panelClass: ['tv-chip-success'],
        });
        this.handleContractCommentSubscription();
      });
  }

  deleteComment(commentId: string): void {
    const confirmationData: TvConfirmationData = {
      title: 'Suppression',
      content: 'Êtes-vous sûr de vouloir supprimer le commentaire ?',
      okButtonTitle: 'Oui',
      cancelButtonTitle: 'Non',
    };

    const dialogRef = this.dialog.open(TvConfirmPopupComponent, {
      data: confirmationData,
      panelClass: 'tv-dialog-container',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(confirmation => {
        if (confirmation !== true) return;

        this.contractCommentService
          .deleteComment(commentId)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.snackBar.open('Commentaire supprimé avec succès', null, {
              duration: 4000,
              verticalPosition: 'top',
              panelClass: ['tv-chip-success'],
            });
            this.handleContractCommentSubscription();
          });
      });
  }

  filterCommentsByDate(): void {
    const startDate = this.screenFilterForm.get('startDate').value;
    const endDate = this.screenFilterForm.get('endDate').value;

    if (!startDate && !endDate) {
      this.filteredComments = [...this.comments];
    } else {
      this.filteredComments = this.comments.filter(comment => {
        const commentDate = new Date(comment.commentDate);
        return (!startDate || commentDate >= startDate) && (!endDate || commentDate <= endDate);
      });
    }

    this.commentIsEmpty = this.filteredComments.length === 0;
  }

  filterComments(searchTerm: string): void {
    const startDate = this.screenFilterForm.get('startDate').value;
    const endDate = this.screenFilterForm.get('endDate').value;

    let filteredComments = [...this.comments];

    if (startDate || endDate) {
      filteredComments = filteredComments.filter(comment => {
        const commentDate = new Date(comment.commentDate);
        return (!startDate || commentDate >= startDate) && (!endDate || commentDate <= endDate);
      });
    }

    if (searchTerm) {
      const term = String(searchTerm).toLowerCase();
      filteredComments = filteredComments.filter(
        comment => (comment.body ?? '').toLowerCase().includes(term) || (comment.author ?? '').toLowerCase().includes(term)
      );
    }

    this.filteredComments = filteredComments;
    this.commentIsEmpty = this.filteredComments.length === 0;
  }
}
